"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertCircle, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

export default function LoginPage() {
    const router = useRouter();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false); // ← только это добавлено для глазика
    const [agreed, setAgreed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [checkingSession, setCheckingSession] = useState(true);

    const [resetLoading, setResetLoading] = useState(false);
    const [resetMessage, setResetMessage] = useState("");

    const handlePasswordReset = async () => {
        if (!email) {
            setResetMessage("Bitte gib zuerst deine Email ein.");
            return;
        }

        setResetLoading(true);
        setResetMessage("");

        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/reset-password`,
            });

            if (error) {
                setResetMessage("Fehler beim Senden der Reset-Mail: " + error.message);
            } else {
                setResetMessage(
                    "Wenn die Email existiert, wurde ein Link zum Zurücksetzen des Passworts gesendet."
                );
            }
        } catch (err) {
            setResetMessage("Unbekannter Fehler beim Zurücksetzen des Passworts.");
        } finally {
            setResetLoading(false);
        }
    };

    const redirectByRole = async () => {
        try {
            const { data } = await supabase.auth.getUser();
            const user = data.user;
            if (!user) {
                setCheckingSession(false);
                return;
            }
            const { data: profile } = await supabase
                .from("profiles")
                .select("role")
                .eq("id", user.id)
                .maybeSingle();

            if (!profile) {
                await supabase.auth.signOut();
                router.replace("/");
                setCheckingSession(false);
                return;
            }

            if (profile.role === "freelancer") {
                router.replace("/freelancer-dashboard");
            } else if (profile.role === "company") {
                router.replace("/company-dashboard");
            }
        } catch (err) {
            await supabase.auth.signOut();
            router.replace("/");
        } finally {
            setCheckingSession(false);
        }
    };

    useEffect(() => {
        const checkSession = async () => {
            try {
                const { data } = await supabase.auth.getSession();
                const session = data.session;
                if (session) {
                    await redirectByRole();
                } else {
                    setCheckingSession(false);
                }
            } catch {
                setCheckingSession(false);
            }
        };

        checkSession();

        const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
            if (session && event === "SIGNED_IN") {
                await redirectByRole();
            }
        });

        return () => listener.subscription.unsubscribe();
    }, []);

    const handleEmailPasswordLogin = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setMessage("");

        if (!agreed) {
            setMessage("Bitte akzeptiere die AGB und Datenschutzbestimmungen.");
            return;
        }

        if (!email || !password) {
            setMessage("Email und Passwort sind erforderlich.");
            return;
        }

        setLoading(true);

        const { error } = await supabase.auth.signInWithPassword({ email, password });

        if (error) {
            setMessage("Login fehlgeschlagen: " + error.message);
        }

        setLoading(false);
    };

    if (checkingSession) return <div className="min-h-screen flex items-center justify-center">Lädt...</div>;

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
                <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">Login</h1>

                <form onSubmit={handleEmailPasswordLogin} className="space-y-6">
                    <div>
                        <Label htmlFor="email" className="text-gray-700 font-medium">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            placeholder="deine@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="mt-1.5 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                        />
                    </div>

                    <div className="relative">
                        <Label htmlFor="password" className="text-gray-700 font-medium">Passwort</Label>
                        <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="mt-1.5 pr-11 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-9 text-gray-500 hover:text-gray-700 focus:outline-none"
                        >
                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                    </div>

                    <div className="flex items-center space-x-2">
                        <Checkbox
                            id="agreed"
                            checked={agreed}
                            onCheckedChange={(checked) => setAgreed(checked as boolean)}
                            className="mt-1 border-gray-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                        />
                        <Label htmlFor="agreed" className="text-sm text-gray-600 cursor-pointer">
                            Ich akzeptiere die{" "}
                            <a href="/agb" className="text-blue-600 underline hover:text-blue-800" target="_blank">
                                AGB
                            </a>{" "}
                            und{" "}
                            <a href="/datenschutz" className="text-blue-600 underline hover:text-blue-800" target="_blank">
                                Datenschutzbestimmungen
                            </a>
                            .
                        </Label>
                    </div>

                    <Button
                        type="submit"
                        disabled={loading}
                        className="w-full h-14 bg-gray-900 hover:bg-black text-white py-6 text-lg font-medium rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                Lädt...
                            </>
                        ) : (
                            "Login"
                        )}
                    </Button>
                </form>

                {message && (
                    <Alert variant="destructive" className="mt-6 rounded-xl">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{message}</AlertDescription>
                    </Alert>
                )}

                <p className="mt-8 text-center text-sm text-gray-600">
                    Noch kein Konto?{" "}
                    <a href="/register/freelancer" className="text-blue-600 underline hover:text-blue-800 font-medium">
                        Für Freelancer registrieren
                    </a>{" "}
                    oder{" "}
                    <a href="/register/company" className="text-blue-600 underline hover:text-blue-800 font-medium">
                        Für Unternehmen registrieren
                    </a>
                </p>

                <div className="mt-6 text-center">
                    <button
                        type="button"
                        onClick={handlePasswordReset}
                        disabled={resetLoading}
                        className="text-sm text-blue-600 underline hover:text-blue-800 font-medium"
                    >
                        {resetLoading ? "Sende Link..." : "Passwort vergessen?"}
                    </button>

                    {resetMessage && (
                        <p className="mt-3 text-sm text-green-600">{resetMessage}</p>
                    )}
                </div>
            </div>
        </div>
    );
}